/* eslint-disable no-shadow */
/* eslint-disable react/sort-comp */
import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import queryString from 'query-string';
import {
  Grid,
  Paper,
  Chip,
  Button,
  Select,
  MenuItem,
  Input
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ListIcon from '@mui/icons-material/List';
import InsertChartIcon from '@mui/icons-material/InsertChart';
import DownloadIcon from '@mui/icons-material/GetApp';
import Pagination from 'components/Pagination';
import DownloadForm from 'components/DownloadForm';
import config from 'config';
import {
  initializeFilters,
  randomId
} from 'utils/commonTools';
import { streamFileToUser } from 'utils/fileTools';
import apiService from 'services/apiService';
import {
  clearError,
  reportError,
} from 'actions/errorActions';
import { changeDocGroupState } from 'actions/docGroupStateAction';
import { setSearchFormFullScreen } from 'actions/searchFormFullScreenActions';
import { translateKeyword, translation } from 'utils/translate';
import FacetBlock from './FacetBlock';
import SearchResults from './SearchResults';
import ResultCharts from './ResultCharts';
import './SearchForm.css';
import EditIcon from '@mui/icons-material/Edit';
import { useLocation, useSearchParams, useNavigate, useParams } from 'react-router-dom';
import RenderSearchForm from './RenderSearchForm';

const sortOptions = [
  'score',
  'date_asc',
  'date_desc',
  'alpha_asc',
  'alpha_desc',
  // old docs don't have timestamp so result would be inaccurate and meaningless
  // 'timestamp_asc',
  'timestamp_desc',
];
const sortDefault = 'alpha_desc';
const sizeOptions = [5, 10, 25, 50, 100, 250, 500];
const sizeDefault = 10;

const formTypes = ['basic', 'proximity', 'prefilter',];

const initialProximityValue = {
  term1: '',
  term2: '',
  slop: 1,
};

const urlifyJson = (value) =>
  encodeURIComponent(JSON.stringify(value));

// returns true if given item has a value and it's not an empty array
const hasContent = (item) =>
  item && !(Array.isArray(item) && !item.length);

const setDefaultSort = (query, docGroup) => {
  if (query.sort) {
    return query.sort
  }

  if (docGroup === 'all') {
    return 'score'
  }

  if (query.term) {
    return 'score'
  }
  return sortDefault
}

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'var(--primary)',
  borderRadius: 5,
  border: 0,
  height: 40,
  marginLeft: 10,
  color: 'white',
  textTransform: 'none',
  fontSize: '16px',
  lineHeight: '18px',
  '&:hover': {
    backgroundColor: 'var(--primary-dark)',
  },
}));

const WhiteButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'white',
  borderRadius: 5,
  border: '3px solid var(--primary)',
  height: 40,
  marginLeft: 10,
  textTransform: 'none',
  color: 'var(--primary)',
  fontSize: '14px',
  lineHeight: '18px',
  '&:hover': {
    backgroundColor: 'var(--blue-verylight)',
  },
}));

const LinkLookButton = styled(Button)({
  backgroundColor: 'white',
  borderRadius: 0,
  border: 'white',
  height: '48px',
  color: 'var(--primary)',
  textDecoration: 'underline',
  textDecorationThickness: 2,
  textTransform: 'none',      
  fontSize: '16px',         
  '&:hover': {
    backgroundColor: 'white',
    textDecoration: 'underline',
    textDecorationThickness: 1,
  },
});


const SearchForm = ({ history }) => {
    const dispatch = useDispatch();

    //global states:
    const { facetConstants, lang, docGroup, searchState } = useSelector(state => ({
    facetConstants: state.facetConstants,
    lang: state.lang,
    docGroup: state.docGroup,
    searchState: state.searchState,
    searchFormFullScreen: state.searchFormFullScreen,
    heQuickLink: state.heQuickLink,
    budgetsQuickLink: state.budgetsQuickLink,
    isSearchSubmited: state.isSearchSubmited,
    }))
    
    const navigate = useNavigate();
    const params= useParams();
    //SearchParams object contains everything in the URL after ? key-value pairs
    const [searchParams, setSearchParams] = useSearchParams();

    const facetFields = config.docGroups[searchParams.get("docGroup")]?.facetFields || config.docGroups.all.facetFields;

    //local states:
    const [submittedTerm, setSubmittedTerm] = useState('');
    const [activeFormType, setActiveFormType] = useState();
    const [sort, setSort] = useState('');
    const [term, setTerm] = useState('');
    const [size, setSize] = useState(sizeDefault);
    const [page, setPage] = useState(1);
    const [pagesTotal, setPagesTotal] = useState(0);
    const [preFilters, setPreFilters] = useState({});
    const [filters, setFilters] = useState({});
    const [proximityValueState, setProximityValueState] = useState();
    const [submittedProximityValue, setSubmittedProximityValue] = useState({});
    const [totalHits, setTotalHits] = useState(facetConstants[docGroup]?.total);
    const [isLoaded, setIsLoaded] = useState(false);
    const [result, setResult] = useState({});
    const [facetCacheForBlocks, setFacetCacheForBlocks] = useState({});
    const [facetCacheForPrefilters, setFacetCacheForPrefilters] = useState({});
    const [showDownloadForm, setShowDownloadForm] = useState(false);
    const [visualize, setVisualize] = useState(false);
    const [loadingDataFromBackend, setLoadingDataFromBackend] = useState(false);
    const [loadingPercent, setLoadingPercent] = useState(0);
    const [fileDownloadLink, setFileDownloadLink] = useState('');
    const [searchSubDocuments, setSearchSubDocuments] = useState(false);
    const [loadingStatus, setLoadingStatus] = useState('initial');
  
    // Create ref-objects
    const searchFormRef = useRef(null);
    const searchInfoRef = useRef(null);

    //Global state manipulating functions: 
    const reportErrorBound = (...args) => dispatch(reportError(...args));
    const clearErrorBound = (...args) => dispatch(clearError(...args));
    const changeDocGroupStateBound = (...args) => dispatch(changeDocGroupState(...args));
    const setSearchFormFullScreenBound = (...args) => dispatch(setSearchFormFullScreen(...args));

    const location = useLocation();
    

    //Function for compounding chosen filters with initialized filters:
    const filterParse = (filters) => {
      let data = {}
      if (typeof filters === 'string') {
          try {
            data = JSON.parse(filters);
          } catch (e) {
             // eslint-disable-next-line
            console.error("Invalid JSON string!", e);
          }
        }
      if (typeof filters === 'object' && filters !== null) {
        data = filters;
      }
      const result = {};
      // eslint-disable-next-line no-restricted-syntax
      for (const key of Object.keys(initializeFilters(facetFields))) {
        if (Object.prototype.hasOwnProperty.call(data, key) && Array.isArray(data[key])) {
          result[key] = data[key];
        } else {
          result[key] = [];
        }
      }
      return result;
    }
   
    // Not like usually in React projects here we update state from the URL because linking is important. 
    // Also every state change has to be changed also in URL.
    // First run when component mounts to initial values from URL:

    useEffect(() => {
      const run = async () => {
        const initialValues = queryString.parse(location.search)
        await setSubmittedTerm(initialValues.term || '')
        await setSort(initialValues.sort || setDefaultSort(docGroup))
        await setTerm(initialValues.term || '')
        await setSize(parseInt(initialValues.size, 10) || sizeDefault)
        await setPage(parseInt(initialValues.page, 10) || 1)
        await setFilters(initialValues.filters ? filterParse(initialValues.filters) : initializeFilters(facetFields))
        await setPreFilters(initialValues.prefilters ? JSON.parse(initialValues.prefilters):null)
        await setSubmittedProximityValue(initialValues.proximity ? JSON.parse(initialValues.proximity) : initialProximityValue)
        await changeDocGroupStateBound(initialValues.docGroup || "all")
        await setActiveFormType(initialValues.activeFormType || "basic")
      
        if (
          initialValues.term ||
          initialValues.term === "" ||
          initialValues.proximity ||
          initialValues.prefilters ||
          initialValues.docGroup
        ) {
          setSearchFormFullScreenBound(true)
          await fetchSearch()
          if (initialValues.docGroup !== docGroup) {
            await setFacetCacheForBlocks(await getFacetCacheForBlock())
          }
        } else {
          setResult({})
        }
      };

      run();
    },[location])

    const resetSearch = async () => {
        setSearchParams({})
      };

    const hasFilters = () => {
        return facetFields.some(
          (field) => filters[field]?.length > 0,
        );
    };
    //function for URL manipulation
    //If key exists, set (replace) its value; if it doesn't exist, append key-value.

    function setOrAppendSearchParams(updates) {
    
      const newParams = new URLSearchParams(searchParams);
      
      Object.entries(updates).forEach(([key, value]) => {
        if (newParams.has(key)) {
          newParams.set(key, value);   // Replace existing value
        } else {
          newParams.append(key, value); // Add new key
        }
      });

      setSearchParams(newParams);
    }
    
    const scrollToResultsTop = () => {
        window.scrollTo({
            top: searchInfoRef.current.offsetTop - 20,
            behavior: 'smooth',
        });
    };
    
    const gotoPage = async (newPage) => {
      setOrAppendSearchParams({page: newPage})
      scrollToResultsTop();
    };

    const fetchSearchAggregations = async() => {
      //This function fetches result the same way as fetchSearch, but without filters and returns only result's aggregations.
      //This is important for facetblocks to be able to show the whole list of filters, not only what was filtered for 
      // also it makes to be able to choose more than one filter from one "bucket".
      let fetchSearchParams = {}
      fetchSearchParams =  Object.fromEntries([...searchParams])
      const { filters, ...paramsWithoutFilters } = fetchSearchParams;
      fetchSearchParams = 'filters' in fetchSearchParams ? paramsWithoutFilters : fetchSearchParams;
      try {
            await setLoadingStatus(loadingStatus === 'initial' ? 'loading' : 'loadingMore')  
            fetchSearchParams = buildSearchParams({withFilters: false})
            let result = null
            try {
              let searchIndex = searchParams.get("docGroup") || "all"
              
              if (searchSubDocuments) {
                searchIndex = docGroup + "_asiakirjat"
              }
      
              result = await apiService.fetchData(
                `search/${searchIndex}${fetchSearchParams}`,
              );

          } catch (error) {
            // eslint-disable-next-line
            console.log(error)
            reportErrorBound(error)
            setLoadingStatus("error");
            return
          }
          clearErrorBound();
          setLoadingStatus("ready")
          return result.aggregations
        } catch (error) {
          // eslint-disable-next-line
          console.log(error)
          setLoadingStatus("error");
        }
    }

    const fetchSearch = async () => {
      let fetchSearchParams = {}
      fetchSearchParams =  Object.fromEntries([...searchParams].filter(([k]) => k !== "filters"));
        try {
            await setLoadingStatus(loadingStatus === 'initial' ? 'loading' : 'loadingMore')  
            fetchSearchParams = buildSearchParams({withFilters:true})
            let result = null
            try {
              let searchIndex = searchParams.get("docGroup") || "all"
              
              if (searchSubDocuments) {
                searchIndex = docGroup + "_asiakirjat"
              }
      
              if (activeFormType === 'semantic') {
                result = await apiService.fetchData(
                  `semsearch/${searchIndex}${fetchSearchParams}`,
                );
              } 
              else {
                result = await apiService.fetchData(
                  `search/${searchIndex}${fetchSearchParams}`,
                );
              }

          } catch (error) {
            setLoadingStatus("error");
            // eslint-disable-next-line
            console.log(error)
            reportErrorBound(error)
            return;
          }

          if (
            typeof result.hits.total === 'object' &&
            'value' in result.hits.total
          ) {
            result.hits.total = result.hits.total.value;
          }

          setIsLoaded(true)

          setPagesTotal(Math.ceil(
            result?.hits?.total / size,
          ))

          setTotalHits(result.hits.total);
          setResult(result);

          clearErrorBound();
          setLoadingStatus("ready")
        } catch (error) {
          setLoadingStatus("error");
          // eslint-disable-next-line
          console.log(error)
        }
    }

    // return search parameter string for API call and browser history push
    const buildSearchParams = ({withFilters = true}) => {
        // withFilters tells what kind of purpose we use this function for.
        // There are two purposes: the basic search what sets the result it uses the filters, so withFilters is true.
        // The other is fetching the aggregations for facetblocks in this case fetching without filters. Aggregations are then in setFacetCacheForBlocks state.
        const parsedSearchParams = Object.fromEntries(searchParams.entries());
        let proximityValues = {}
        if (parsedSearchParams.proximity){
          proximityValues = JSON.parse(parsedSearchParams.proximity) || null
        }
        let builtParams = {}
        try {
          builtParams = {
            size: parsedSearchParams.size || sizeDefault,
            sort: parsedSearchParams.sort || null,
            prefilters: parsedSearchParams.prefilters || null,
            lang: parsedSearchParams.lang || "fi",
            visualize: visualize ? 1 : null,
            proximity:
                  proximityValues.term1 &&
                  proximityValues.term2 &&
                  urlifyJson(proximityValues),
            page: parsedSearchParams.page > 1 && parsedSearchParams.page,
            term: parsedSearchParams.term || null,
          }
        }catch(error){
          // eslint-disable-next-line
          console.log("error: ", error)
        }
        //filters in API request has to be spread. Like -> year:["2017"],asiasanat:["import"], instead of -> filters:{year:["2017"], asiasanat:["import"]}
        if ("filters" in parsedSearchParams && withFilters){
          const filtersObj = { ...parsedSearchParams};
          if (
            typeof filtersObj.filters === 'string' &&
            filtersObj.filters.trim().startsWith('{') &&
            filtersObj.filters.trim().endsWith('}')
          ) {
            try {
              const filterObj = JSON.parse(filtersObj.filters);
              // replace all spaces in status field with +-characters
              // as they are space sensitive, and trailing spaces are lost
              // during API requests.
              // see issue #151 for more
              if ("asian_tila" in filterObj) {
                filterObj["asian_tila"] = filterObj["asian_tila"]
                  .map((filter) => filter.replaceAll(" ", "+"))
              }
              Object.assign(builtParams, filterObj);
            } catch (e) {
              // eslint-disable-next-line
              console.error("Invalid JSON in 'filters':", e);
            }
          }
          delete filtersObj.filters;
        }
        if (withFilters){
          try {
              setSearchSubDocuments((filters["asiakirjat.tyyppi"] && filters["asiakirjat.tyyppi"].length > 0) ? true : false)
              setTotalHits((filters["asiakirjat.tyyppi"] && filters["asiakirjat.tyyppi"].length > 0) ? facetConstants[docGroup + '_asiakirjat']?.total : facetConstants[docGroup]?.total)
          } catch (error) {
            // eslint-disable-next-line
              console.log("error: ", error)
              setSearchSubDocuments(false)
          }
        }
    
        return `?${Object.entries(builtParams)
        .filter((entry) => hasContent(entry[1]))
        .flatMap(([key, value]) =>
            Array.isArray(value)
            ? value.map((item) => `${key}=${item}`)
            : `${key}=${value}`,
        )
        .join('&')}`;
    };
    const handleTermChange = (event) =>{
        setTerm(event.target.value)
        setProximityValueState(initialProximityValue)
    }

    const handleBasicSearchSubmit = async (event) => {
        event.preventDefault();
        setSearchFormFullScreenBound(true)
        let sortValue= "date_desc"
        if (term){
          sortValue = "score"
        }
        setOrAppendSearchParams({term: term, sort: sortValue, lang: lang, status: searchState, activeFormType: "basic", page: '1'})
    }

    //DockGroup selection:
    const handleSelectOnClick = async (event) => {
        setOrAppendSearchParams({docGroup: event.target.value})
    }

    const handlePrefilterDockGroupChange = (selectedDocGroup) => {
      setOrAppendSearchParams({docGroup: selectedDocGroup})
      
    }
    const handleResultSizeChange = async (event) => {
      if (size !== event.target.value) {
        setOrAppendSearchParams({page: "1", size: event.target.value})
        scrollToResultsTop();
      }
    };
    const handleResultSortChange = async (event) => {
      if (sort !== event.target.value) {
        setOrAppendSearchParams({sort: event.target.value, page: "1"})
        scrollToResultsTop();
      }
    };
    
    //Have to make an extra api request to get all the facets of the chosen docGroup without filters.
    const getFacetCacheForBlock = async () => {
      const resultWithoutFilters = await fetchSearchAggregations()
      return resultWithoutFilters
    }

    // functions for facets
    const handleFacetChange = async (field, itemKey) => {
      const strValue = `${itemKey}`;
      const prev = filters && filters[field] ? filters[field] : [];
      const newValues = prev.includes(strValue)
        ? prev.filter(item => item !== strValue)
        : [...prev, strValue];

      // Create a new object with only non-empty arrays
      const newFilters = Object.fromEntries(
        Object.entries({
          ...filters,
          [field]: newValues,
        }).filter(([_, arr]) => arr && arr.length > 0)
      );
      setOrAppendSearchParams({page: "1", filters: JSON.stringify(newFilters)})
    };

    const handleSelectAllButTHisFacet = async (itemKey, field) => {

      const strValue = `EI ${itemKey}`;
      const prev = filters && filters[field] ? filters[field] : [];
      const newValues = prev.includes(strValue)
        ? prev.filter(item => item !== strValue)
        : [...prev, strValue];

      // Create a new object with only non-empty arrays
      const newFilters = Object.fromEntries(
        Object.entries({
          ...filters,
          [field]: newValues,
        }).filter(([_, arr]) => arr && arr.length > 0)
      );
      setOrAppendSearchParams({page: "1", filters: JSON.stringify(newFilters)})
    }

    const removeAllFacets = async () => {
      setOrAppendSearchParams({filters:JSON.stringify({})})
    };

     // functions for preFilters
    const handlePreFiltersSubmit = async (
      event,
      preFiltersInput,
      docGroup,
    ) => {
      event.preventDefault();
      // filter out empty strings and arrays from preFiltersInput
      setSearchFormFullScreenBound(true)
      
      const filteredInput = Object.entries(
          preFiltersInput,
      ).reduce(
        (soFar, [key, value]) =>
          hasContent(value)
            ? {
              ...soFar,
              [key]: value,
            }
            : soFar,
        {},
      );
      const sParams = { docGroup: docGroup, sort: "alpha_desc", prefilters: filteredInput, lang: lang, status: searchState, activeFormType: "basic" };

      let prefParams = {};
      Object.entries(sParams).forEach(([key, value]) => {
        if (key === "prefilters" && value && typeof value === "object" && !Array.isArray(value)) {
          prefParams.prefilters = JSON.stringify(value);
        } else if (value !== undefined && value !== null) {
          prefParams[key] = value;
        }
      });
      setOrAppendSearchParams(prefParams)
    };

    const toggleDownloadForm = () => {
      setShowDownloadForm(!showDownloadForm)
      setFileDownloadLink('')
      setLoadingDataFromBackend(false)
    };

    const toggleVisualize = () => {
      setVisualize(!visualize)
      setShowDownloadForm(false)
  
      if (!visualize) {
        const params = new URLSearchParams(
          window.location.search,
        );
        params.set('visualize', 1);
        const newRelativePathQuery = `${window.location.pathname
          }?${params.toString()}`;
        window.history.pushState(
          null,
          '',
          newRelativePathQuery,
        );
      } else {
        const params = new URLSearchParams(
          window.location.search,
        );
        params.delete('visualize');
        const newRelativePathQuery = `${window.location.pathname
          }?${params.toString()}`;
        window.history.pushState(
          null,
          '',
          newRelativePathQuery,
        );
      }
    };

    const downloadSearchResults = async ({
      metaFormat,
      fileFormats,
    }) => {
      // show spinner
        setLoadingDataFromBackend(true)

      let jobId = '';
      if (fileFormats) {
        jobId = randomId(8);
      }
      const parsedSearchParams = Object.fromEntries(searchParams.entries());
        let proximityValues = {}
        if (parsedSearchParams.proximity){
          proximityValues = JSON.parse(parsedSearchParams.proximity) || null
        }
        let builtParams = {}
        try {
          builtParams = {
            size: parsedSearchParams.size || sizeDefault,
            sort: parsedSearchParams.sort || null,
            prefilters: parsedSearchParams.prefilters || null,
            lang: parsedSearchParams.lang || "fi",
            visualize: visualize ? 1 : null,
            proximity:
                  proximityValues.term1 &&
                  proximityValues.term2 &&
                  urlifyJson(proximityValues),
            page: page > 1 && page,
            term: parsedSearchParams.term || null,
          }
        }catch(error){
          // eslint-disable-next-line
          console.log("error: ", error)
        }

        //filters in API request has to be spread. Like -> year:["2017"],asiasanat:["import"], instead of -> filters:{year:["2017"], asiasanat:["import"]}
        if ("filters" in parsedSearchParams){
          const filtersObj = { ...parsedSearchParams};
          if (
            typeof filtersObj.filters === 'string' &&
            filtersObj.filters.trim().startsWith('{') &&
            filtersObj.filters.trim().endsWith('}')
          ) {
            try {
              const filterObj = JSON.parse(filtersObj.filters);
              Object.assign(builtParams, filterObj);
            } catch (e) {
              // eslint-disable-next-line
              console.error("Invalid JSON in 'filters':", e);
            }
          }
          delete filtersObj.filters;
        }

      const downloadSearchParams = `?${Object.entries(builtParams)
        .filter((entry) => hasContent(entry[1]))
        .flatMap(([key, value]) =>
            Array.isArray(value)
            ? value.map((item) => `${key}=${item}`)
            : `${key}=${value}`,
        ).join('&')}`;
      try {
        const result = await apiService.fetchData(
          `download/${docGroup}${downloadSearchParams}&format=${metaFormat}&content=${fileFormats}&job_id=${jobId}`,
          'text',
        );
  
        if (result) {
          streamFileToUser({
            data: result,
            format: metaFormat,
            fileName: `${docGroup}_exported`,
          });
        }
  
        if (fileFormats) {
          const interval = setInterval(async () => {
            try {
              const { status } =
                await apiService.checkFileStatus(jobId);
  
              if (status === 'DONE') {
                clearInterval(interval);
                setFileDownloadLink(jobId)
                setLoadingDataFromBackend(false)
                // eslint-disable-next-line no-restricted-globals
              } else if (!isNaN(status)) {
                  setLoadingPercent(parseFloat(status))
              } else {
                clearInterval(interval);
                  setLoadingDataFromBackend(false)
                  setShowDownloadForm(false)
                reportErrorBound(status);
              }
            } catch (err) {
              // eslint-disable-next-line
              console.log(err);
              // pass (this error will be handled by parent)
            }
          }, 3000);
        } else {
            setLoadingDataFromBackend(false)
            setShowDownloadForm(false)
        }
      } catch (error) {
          setLoadingDataFromBackend(false)
          setShowDownloadForm(false)
          reportErrorBound(error);
      }
    };

    const handleDownloadZippedFile = () => {
      setLoadingDataFromBackend(false)
    };

    const handlePrefilterButtonSubmit = () => {
      setOrAppendSearchParams({activeFormType: "prefilter"})
    }

    const renderResultChooserArea = () => {
      if (!isLoaded || pagesTotal <= 0) {
        return null;
      }
      return(
          <Grid container className="result-chooser-area">
            <Grid item xs={12} sm={12} md={4} className="result-chooser-wrapper-totalhits">
              {/* hit stats */}
              {translation("results", "hits", lang)} {result?.hits?.total} {translation("search", "pieces", lang)}
            </Grid>
            <Grid item xs={12} sm={12} md={8} className = "result-chooser-grid" >
              <Grid container spacing={1}>
                <Grid item xs={12} sm={12} md={1}></Grid>
                <Grid item xs={12} sm={12} md={7} className = "select">
                  {!visualize &&
                  <>
                    <div className='select-order'>
                      {translation("results", "order", lang)}:
                      <Select
                          className="search-form-result-chooser"
                          value={sort}
                          onChange={handleResultSortChange}
                          input={<Input disableUnderline />}
                        >
                          {sortOptions.map((value) => (
                              <MenuItem value={value} key={value}>
                                {translateKeyword(value, lang)}
                              </MenuItem>
                            ),
                          )}
                      </Select>
                    </div>
                    <div className= "select-number">
                      {translation("results", "on_page", lang)}:
                      <Select
                          className="search-form-result-chooser"
                          value={size}
                          onChange={handleResultSizeChange}
                          input={<Input disableUnderline />}
                        >
                          {sizeOptions.map((value) => (
                              <MenuItem value={value} key={value}>
                                {translateKeyword(value, lang)}
                              </MenuItem>
                            ),
                          )}
                      </Select>
                    </div>
                  </>
                  }
                </Grid>
                <Grid item xs={12} sm={12} md={4} >
                  {renderResultTools()}
                </Grid>
              </Grid>
            </Grid>    
          </Grid>
      )
    }
     // search result list, pagination and sorting etc. tools for rendering
  const renderSearchResultsArea = () => {
    if (!isLoaded || pagesTotal <= 0) {
      return null;
    }
    const { hits } = result?.hits;
    let pagination = null;
    if (pagesTotal > 1) {
      pagination = (
        <Paper elevation={0}>
          <Pagination
            page={page}
            pagesTotal={pagesTotal}
            gotoPage={gotoPage}
          />
        </Paper>
      );
    }
    return (
      <div id="results" className="results-area">
        {/* {pagination} */}
        <Paper elevation={0}>
          <SearchResults
            hits={hits}
            docGroup={docGroup}
            highlightTerm={
              activeFormType === 'proximity'
                ? `${submittedProximityValue.term1}%20OR%20${submittedProximityValue.term2}`
                : submittedTerm
            }
            page={page}
            size={size}
            searchTerm={submittedTerm? submittedTerm : ''}
          />
        </Paper>
        {pagination}
      </div>
    );
  }
   // info text area from current search and preFilters for rendering
  const renderSearchInfo = () => {

    if (!result.hits || !isLoaded) {
      return null;
    }
    const activePreFilterNames = preFilters? Object.entries(preFilters)
      .filter((keyValuePair) => hasContent(keyValuePair[1]))
      .map(([key, value]) => {
        const label = translateKeyword(key, lang);
        const selection = Array.isArray(value)
          ? value.join(', ')
          : value;
        return `${label} (${selection})`;
      })
      .join(', ')
      : null;

    return (
      <>
        {activePreFilterNames && activeFormType != "prefilter" &&
          <LinkLookButton
            id="prefilters-toggler"
            className="prefilters-toggler"
            disableTouchRipple
            onClick={handlePrefilterButtonSubmit}
          >
            <EditIcon className="button-icon" />
            {translation("prefilter", "button", lang)}
          </LinkLookButton>
        }
        {activePreFilterNames && (
          <div className="search-info">
            <strong>{translation("results", "prefilters", lang)}:</strong>{' '}
            <i>{activePreFilterNames}</i>
          </div>
        )}
        <div className="search-info">
          {/* if basic search with term */}
          {submittedTerm && (
            <>
              {/* {translation("results", "term", lang)}: <strong>{submittedTerm}</strong>,{' '} */}
              <span className='search-info-black'>{translation("Näytetään_tulokset_haulla", lang)} </span>
              <span className="search-info-blue">"{submittedTerm}"</span>
            </>
          )}
          {/* if proximity search */}
          {submittedProximityValue.term1 &&
            submittedProximityValue.term2 && (
              <div>
                {translation("search", "proximity", "name", lang)}:{' '}
                <strong>
                  {submittedProximityValue.term1}
                </strong>{' '}
                {'<max '} {submittedProximityValue.slop}{' '}
                {' ' + translation("search", "proximity", "words", lang) + '>'}{' '}
                <strong>
                  {submittedProximityValue.term2}
                </strong>
                ,{' '}
              </div>
            )}
        </div>
      </>
    );
  }
      
    const renderResultTools = () => {
      return (
        result &&
        result.hits &&
        result.hits.total > 0 && (
          <div className="button-area">
            {!visualize &&
            <StyledButton
              disableTouchRipple
              type="submit"
              onClick={toggleDownloadForm}
              variant="contained"
            >
              <DownloadIcon className="button-icon" />
              {translation("results", "download", lang)}
            </StyledButton>
            }
            <StyledButton
              disableTouchRipple
              variant="contained"
              onClick={toggleVisualize}
            >
              {visualize ? (
                <>
                  <ListIcon className="button-icon" />
                    {translation("results", "show_results", lang)}
                </>
              ) : (
                <>
                  <InsertChartIcon className="button-icon" />
                    {translation("results", "show_visual", lang)}
                </>
              )}
            </StyledButton>
          </div>
        )
      );
    }
    // active facet chips (if any) for rendering
    const renderActiveFacets = () => {
      if (!isLoaded || !hasFilters()) {
        return null;
      }
      return (
        <div className="facet-active-wrapper">
          {facetFields.map(
            (field) =>
              filters[field] &&
              filters[field].map((item) => (<>
                <Chip
                  key={item}
                  label={translateKeyword(item, lang)}
                  onDelete={() =>
                    handleFacetChange(field, item)
                  }
                  className="facet-chip"
                />
                </>
              )),
          )}
          <Chip
            label={translation("results", "remove_active_facets", lang)}
            onClick={() => removeAllFacets()}
            className="facet-chip remove-all"
          />
        </div>
      );
    }
    // facet blocks for rendering
    const renderFacetBlocks = () => {
      if (
        !isLoaded ||
        (pagesTotal <= 0 && !hasFilters())
      ) {
        return null;
      }
      return (
        <div className="facets">
          <h4>{translation("Rajaa_hakua", lang)}</h4>
          {facetFields.map(
            (field) => (
              result?.aggregations[field] && (
                <FacetBlock
                  key={field}
                  field={field}
                  onChange={handleFacetChange}
                  selectAllButThisFacet={handleSelectAllButTHisFacet}
                  facets={result.aggregations[field].buckets}
                  cachedFacets={field in facetCacheForBlocks ? facetCacheForBlocks[field].buckets : []}
                  filters={filters[field]}
                />
              )
            )
          )}
          <div className="white-button">
            <WhiteButton
              type="submit"
              onClick={() => removeAllFacets()}
            >
              {translation("Tyhjennä valinnat", lang)}
            </WhiteButton>
          </div>
        </div>
      );
    }
    // Render it all
    return (
        <div className= "search" role="main">
          <RenderSearchForm
            totalHits={totalHits}
            formTypes={formTypes}
            activeFormType={activeFormType}
            searchFormRef={searchFormRef}
            handleBasicSearchSubmit={handleBasicSearchSubmit}
            term={term}
            handleTermChange={handleTermChange}
            handleSelectOnClick={handleSelectOnClick}
            config={config}
            loadingStatus={loadingStatus}
            preFilters={preFilters}
            handlePreFiltersSubmit={handlePreFiltersSubmit}
            resetSearch={resetSearch}
            handlePrefilterDockGroupChange={handlePrefilterDockGroupChange}
          />
          
          <div
            className="search-info-area"
            ref={searchInfoRef}
          >
            {renderSearchInfo()}
            {renderActiveFacets()}
          </div>
          
          {showDownloadForm ? (
            <div className="download-form-wrapper download-form-wrapper--open">
              <DownloadForm
                loadingDataFromBackend={
                  loadingDataFromBackend
                }
                loadingPercent={loadingPercent}
                fileDownloadLink={fileDownloadLink}
                handleDownload={downloadSearchResults}
                handleDownloadZippedFile={
                  handleDownloadZippedFile
                }
              />
            </div>
          ) : (
            <div className="download-form-wrapper" />
          )}

          {visualize ? (
            // Show either search data visualization...
            result &&
            result.hits &&
            result.hits.total > 0 && (
              <Grid container spacing={4}>
                <Grid item xs={12} sm={12} md={12}>
                  {renderResultChooserArea()}
                  <ResultCharts
                    docGroup={docGroup}
                    dataset={result.aggregations || {}}
                    cache={facetCacheForPrefilters}
                    handleFacetChange={handleFacetChange}
                  />
                </Grid>
              </Grid>
            )
          ) : 
          (
            // ...or search results and facets
            <Grid container spacing={1}>
              <Grid item xs={12} sm={12} md={12}>
                
                {Object.keys(result).length>0 &&
                  renderResultChooserArea()
                }
              </Grid>
              <Grid item xs={12} sm={12} md={3} className='facet-grid'>
                {Object.keys(result).length>0 &&
                  renderFacetBlocks()
                }
              </Grid>
              <Grid item xs={12} sm={12} md={9}>
                {Object.keys(result).length>0 &&
                  renderSearchResultsArea()
                }
              </Grid>
            </Grid>
          )}
            
        </div>
    )

    // SearchForm.propTypes = {
    //     docGroup: PropTypes.string.isRequired,
    //     facetConstants: PropTypes.shape({}).isRequired,
    //     lang: PropTypes.string,
    //     location: PropTypes.shape({
    //       search: PropTypes.string,
    //     }).isRequired,
    //     clearError: PropTypes.func.isRequired,
    //     reportError: PropTypes.func.isRequired,
    //     history: PropTypes.shape({
    //       push: PropTypes.func,
    //     }).isRequired,
    // };
     
  }
  export default SearchForm;
